import React, { forwardRef, useId } from 'react';
import { GeometryArc, GeometryDot, TradeMapGeometry } from './geometry';
import { directionColor, THEME } from './theme';
import { TradeMapFilter, TradeMapVariant } from './types';
import './TradeMapSvg.css';

// The live profile map. Paints the geometry as <path d>, with every colour,
// width and dash as an inline attribute from theme.ts. The stylesheet holds
// only motion, dim states and layout, so hover and the chips are class names
// and CSS, never a re-render per arc.
//
// Layers, bottom to top: base map, arcs (completed and live), package dots,
// partner dots and rings, home. A package therefore rides above the arcs and
// passes UNDER the destination dot when it arrives (round 2 spec).

/** Past this many live arcs, the rest render as static dashes with no dot: an animation per arc stops being free */
export const MAX_ANIMATED_LIVE = 20;

/** The package dot's per-size multiplier (round 2 spec): 1 on desktop, 0.55 on phones */
export const packageScale = (variant: TradeMapVariant): number => (variant === 'mobile' ? 0.55 : 1);

interface Props {
  geometry: TradeMapGeometry;
  filter?: TradeMapFilter;
  /** Reduced motion: no draw-on, no march, no package dot; live arcs stay as static dashes */
  static?: boolean;
  /** Off-screen: CSS animations pause (SMIL is paused by the container through the ref) */
  paused?: boolean;
  /** Desktop only: hover dimming and the tooltip callback */
  interactive?: boolean;
  hoveredKey?: string | null;
  onHover?: (key: string | null) => void;
  ariaLabel: string;
}

const safeId = (raw: string) => raw.replace(/[^a-zA-Z0-9_-]/g, '');

/** Loop lengths per partner (3.2, 3.7, 4.2s), start times staggered so dots never move in sync */
const packageTiming = (liveIndex: number) => {
  const dur = [3.2, 3.7, 4.2][liveIndex % 3];
  const begin = -((liveIndex * 0.9) % dur);
  return { dur, begin };
};

const ArcPaths = React.memo(function ArcPaths({ arc, motion, animateLive, active, uid }: { arc: GeometryArc; motion: boolean; animateLive: boolean; active: boolean; uid: string }) {
  const color = directionColor(arc.direction);
  const activeClass = active ? ' is-active' : '';
  return (
    <>
      {arc.count > 0 && (
        <path
          className={`ptm-arc ptm-arc--${arc.direction}${motion ? ' ptm-arc--draw' : ''}${activeClass}`}
          d={arc.d}
          pathLength={1}
          fill="none"
          stroke={color}
          strokeWidth={arc.width}
          strokeLinecap="butt"
          opacity={THEME.completedArcOpacity}
          data-partner-key={arc.partnerKey}
          data-count={arc.count}
        />
      )}
      {arc.live > 0 && (
        <path
          id={`${uid}-travel-${safeId(arc.key)}`}
          className={`ptm-arc-live ptm-arc-live--${arc.direction}${animateLive ? ' ptm-arc-live--march' : ''}${activeClass}`}
          d={arc.travelD}
          pathLength={1}
          fill="none"
          stroke={color}
          strokeWidth={THEME.liveStrokeWidth}
          strokeDasharray={THEME.liveDashArray}
          strokeLinecap="round"
          data-partner-key={arc.partnerKey}
          data-live={arc.live}
        />
      )}
    </>
  );
});

/**
 * The package (round 2 spec): a blurred filled circle in the arc colour at 90%
 * under a solid white core, riding the live arc toward the recipient. Radius
 * 5 x scale + 1 (glow) and 2.4 x scale + 0.6 (core); blur 2.2 x scale + 0.6.
 */
const Package = React.memo(function Package({ arc, liveIndex, scale, active, uid }: { arc: GeometryArc; liveIndex: number; scale: number; active: boolean; uid: string }) {
  const { dur, begin } = packageTiming(liveIndex);
  return (
    <g className={`ptm-package${active ? ' is-active' : ''}`} data-partner-key={arc.partnerKey} aria-hidden data-testid="ptm-package">
      <circle r={5 * scale + 1} fill={directionColor(arc.direction)} fillOpacity={THEME.packageGlowOpacity} filter={`url(#${uid}-package-blur)`} className="ptm-package-glow" />
      <circle r={2.4 * scale + 0.6} fill={THEME.packageCore} className="ptm-package-core" />
      <animateMotion dur={`${dur}s`} begin={`${begin.toFixed(2)}s`} repeatCount="indefinite" rotate="0">
        <mpath href={`#${uid}-travel-${safeId(arc.key)}`} />
      </animateMotion>
    </g>
  );
});

const Dot = React.memo(function Dot({ dot, active, interactive, onHover }: { dot: GeometryDot; active: boolean; interactive: boolean; onHover?: (key: string | null) => void }) {
  const live = dot.partner.liveInbound > 0 ? 'inbound' : dot.partner.liveOutbound > 0 ? 'outbound' : null;
  const activeClass = active ? ' is-active' : '';
  return (
    <g
      className={`ptm-partner${activeClass}`}
      data-partner-key={dot.key}
      onMouseEnter={interactive && onHover ? () => onHover(dot.key) : undefined}
      onMouseLeave={interactive && onHover ? () => onHover(null) : undefined}
    >
      <circle className="ptm-dot" cx={dot.x} cy={dot.y} r={dot.r} fill={THEME.dotFill} stroke={THEME.dotStroke} strokeWidth={THEME.dotStrokeWidth} />
      {live && <circle className="ptm-ring" cx={dot.x} cy={dot.y} r={dot.r + 3} fill="none" stroke={directionColor(live)} strokeWidth={THEME.liveRingWidth} />}
    </g>
  );
});

const TradeMapSvg = forwardRef<SVGSVGElement, Props>(
  ({ geometry, filter = 'all', static: isStatic = false, paused = false, interactive = false, hoveredKey = null, onHover, ariaLabel }, ref) => {
    const uid = safeId(useId());
    const motion = !isStatic;
    const scale = packageScale(geometry.variant);
    // Busiest partners first (model order), so the animation cap is deterministic
    const liveOrder = new Map<string, number>();
    geometry.arcs.forEach((arc) => {
      if (arc.live > 0) liveOrder.set(arc.key, liveOrder.size);
    });
    const liveIndexOf = (arc: GeometryArc) => liveOrder.get(arc.key) ?? -1;
    const animates = (arc: GeometryArc) => motion && arc.live > 0 && liveIndexOf(arc) < MAX_ANIMATED_LIVE;

    return (
      <svg
        ref={ref}
        className={`ptm-svg${hoveredKey ? ' ptm-map--hover' : ''}${paused ? ' ptm-map--paused' : ''}`}
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        data-filter={filter}
        data-static={isStatic}
        data-testid="profile-trade-map-svg"
      >
        <defs>
          <filter id={`${uid}-package-blur`} x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation={2.2 * scale + 0.6} />
          </filter>
        </defs>
        <g className="ptm-base">
          <path d={geometry.graticuleD} fill="none" stroke={THEME.graticule} strokeWidth={THEME.graticuleWidth} />
          {geometry.countries.map((country) => (
            <path
              key={country.id}
              d={country.d}
              fill={country.traded ? THEME.landTraded : THEME.land}
              stroke={country.traded ? THEME.landTradedStroke : THEME.landStroke}
              strokeWidth={THEME.landStrokeWidth}
              data-traded={country.traded || undefined}
            />
          ))}
          <path d={geometry.bordersD} fill="none" stroke={THEME.landStroke} strokeWidth={THEME.landStrokeWidth} />
        </g>
        <g className="ptm-arcs">
          {geometry.arcs.map((arc) => (
            <ArcPaths key={arc.key} arc={arc} motion={motion} animateLive={animates(arc)} active={hoveredKey === arc.partnerKey} uid={uid} />
          ))}
        </g>
        {motion && (
          <g className="ptm-packages">
            {geometry.arcs.filter(animates).map((arc) => (
              <Package key={arc.key} arc={arc} liveIndex={liveIndexOf(arc)} scale={scale} active={hoveredKey === arc.partnerKey} uid={uid} />
            ))}
          </g>
        )}
        <g className="ptm-dots">
          {geometry.dots.map((dot) => (
            <Dot key={dot.key} dot={dot} active={hoveredKey === dot.key} interactive={interactive} onHover={onHover} />
          ))}
        </g>
        {geometry.home && (
          <g className="ptm-home">
            <circle cx={geometry.home.x} cy={geometry.home.y} r={THEME.homeHaloRadius} fill="none" stroke={THEME.homeHalo} strokeWidth={THEME.homeHaloWidth} />
            <circle cx={geometry.home.x} cy={geometry.home.y} r={THEME.homeRadius} fill={THEME.home} />
          </g>
        )}
      </svg>
    );
  }
);

TradeMapSvg.displayName = 'TradeMapSvg';

export default TradeMapSvg;
